import React, { useState, useCallback } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  IconButton,
  Box,
  Tooltip,
  Select,
  Typography,
  MenuItem,
  Grid,
  Collapse,
  Chip,
} from "@mui/material";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { isTestEnv } from "../../globals";

const OrderTable = React.memo(
  ({
    order,
    orderIndex,
    handleQuantityChange,
    handleDeltaCheckboxChange,
    handleLineCheckboxChange,
    fetchSerialNumbers,
    serialNumbers,
    updateOrderData,
  }) => {
    const [expandedRows, setExpandedRows] = useState({});
    const [loadingSerials, setLoadingSerials] = useState({});
    const [batchValues, setBatchValues] = useState({});
    const [noteValues, setNoteValues] = useState({});

    const toggleRowExpansion = useCallback(
      async (lineIndex, partname) => {
        setExpandedRows((prev) => {
          const isExpanded = !prev[lineIndex];
          return { ...prev, [lineIndex]: isExpanded };
        });

        if (!serialNumbers[partname]) {
          setLoadingSerials((prev) => ({ ...prev, [lineIndex]: true }));
          try {
            await fetchSerialNumbers(partname);
          } catch (error) {
            isTestEnv &&
              console.error("Failed to fetch serial numbers:", error);
          } finally {
            setLoadingSerials((prev) => ({ ...prev, [lineIndex]: false }));
          }
        }
      },
      [fetchSerialNumbers, serialNumbers],
    );

    const handleBatchChange = (lineIndex, value) => {
      setBatchValues((prev) => ({ ...prev, [lineIndex]: value }));
      updateOrderData(orderIndex, lineIndex, { batch: value });
    };

    const handleNoteChange = (lineIndex, value) => {
      setNoteValues((prev) => ({ ...prev, [lineIndex]: value }));
      updateOrderData(orderIndex, lineIndex, { note: value });
    };

    return (
      <Box bgcolor="#fafafa" borderRadius={2} marginY={2} p={2}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: "8%" }}>פריט</TableCell>
                <TableCell sx={{ width: "12%" }}>תיאור</TableCell>
                <TableCell sx={{ width: "8%" }}>מחיר ליחידה</TableCell>
                <TableCell sx={{ width: "8%" }}>כמות בהזמנה</TableCell>
                <TableCell sx={{ width: "8%" }}>כמות מאושרת</TableCell>
                <TableCell sx={{ width: "7%" }}>מ. זמין למכירה</TableCell>
                <TableCell sx={{ width: "7%" }}>ממוצע מכירות 6 חודשים אחרונים</TableCell>
                <TableCell sx={{ width: "7%" }}>כמות מכירות חודש שעבר</TableCell>
                <TableCell sx={{ width: "7%" }}>כמות מכירות חודש נוכחי</TableCell>
                <TableCell sx={{ width: "5%" }}>הזמנת יתרה</TableCell>
                <TableCell sx={{ width: "10%" }}>מורשים לאישור</TableCell>
                <TableCell sx={{ width: "5%" }}>אשר</TableCell>
                <TableCell sx={{ width: "4%" }}></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {order.lines?.map((line, lineIndex) => (
                <React.Fragment key={`${orderIndex}-${lineIndex}`}>
                  <TableRow
                    sx={{
                      backgroundColor: line.isDuplicate
                        ? "#fff3cd"
                        : line.checked
                          ? "#f0f0f0"
                          : "transparent",
                      borderLeft: line.isDuplicate
                        ? "4px solid #ff9800"
                        : "none",
                      "& > *": { borderBottom: "unset" },
                    }}
                  >
                    <TableCell style={{ fontWeight: "bold" }}>
                      <Box display="flex" alignItems="center" gap={1}>
                        {line.partname}
                        {line.isDuplicate && (
                          <Tooltip title="כפילות - פריט ולקוח זהים בהזמנות אחרות">
                            <Chip
                              icon={<WarningAmberIcon />}
                              label="כפילות"
                              size="small"
                              color="warning"
                              sx={{ height: "20px", fontSize: "0.7rem" }}
                            />
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>{line.partdes}</TableCell>
                    <TableCell>{line.disprice}</TableCell>
                    <TableCell>
                      {line.quant} ({line.tunitname})
                    </TableCell>
                    <TableCell>
                      <TextField
                        type="number"
                        value={
                          line.newQuant !== undefined
                            ? line.newQuant
                            : line.quant
                        }
                        onChange={(event) =>
                          handleQuantityChange(orderIndex, lineIndex, event)
                        }
                        size="small"
                        sx={{
                          minWidth: "7rem", // rem units are more stable than vw
                          width: "auto",
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      {line.balance} ({line.unitname4})
                    </TableCell>
                    <TableCell>
                      {line.salesData?.factoryQuantityLast6Avg?.toLocaleString('he-IL') || 0}
                    </TableCell>
                    <TableCell>
                      {line.salesData?.factoryQuantityLast1?.toLocaleString('he-IL') || 0}
                    </TableCell>
                    <TableCell>
                      {line.salesData?.factoryQuantityCurrent?.toLocaleString('he-IL') || 0}
                    </TableCell>
                    <TableCell>
                      <IconButton
                        onClick={() =>
                          handleDeltaCheckboxChange(orderIndex, lineIndex)
                        }
                        disabled={
                          line.newQuant === undefined || line.newQuant == 0
                        }
                      >
                        {line.deltaEnabled ? (
                          <TaskAltIcon sx={{ color: "#217346" }} />
                        ) : (
                          <RadioButtonUncheckedIcon />
                        )}
                      </IconButton>
                    </TableCell>
                    <TableCell>
                      {line.neo_authoemail}
                      <br />
                      {line.neo_authoemail2}
                    </TableCell>
                    <TableCell>
                      <Tooltip title="מאושר">
                        <IconButton
                          onClick={() =>
                            handleLineCheckboxChange(orderIndex, lineIndex)
                          }
                        >
                          {line.checked ? (
                            <TaskAltIcon sx={{ color: "#217346" }} />
                          ) : (
                            <RadioButtonUncheckedIcon />
                          )}
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <IconButton
                        onClick={() =>
                          toggleRowExpansion(lineIndex, line.partname)
                        }
                      >
                        {expandedRows[lineIndex] ? (
                          <ExpandLessIcon />
                        ) : (
                          <ExpandMoreIcon />
                        )}
                      </IconButton>
                    </TableCell>
                  </TableRow>
                  {expandedRows[lineIndex] && (
                    <TableRow
                      sx={{
                        backgroundColor: line.isDuplicate
                          ? "#fff3cd"
                          : line.checked
                            ? "#f0f0f0"
                            : "transparent",
                      }}
                    >
                      <TableCell
                        style={{ paddingBottom: 0, paddingTop: 0 }}
                        colSpan={12}
                      >
                        <Collapse
                          in={expandedRows[lineIndex]}
                          timeout="auto"
                          unmountOnExit
                        >
                          <Box sx={{ margin: 1 }}>
                            <Box display="flex" gap={2}>
                              <Box>
                                <Typography variant="caption">אצווה</Typography>
                                <Select
                                  size="small"
                                  fullWidth
                                  sx={{ mt: 1, minWidth: "200px" }}
                                  value={batchValues[lineIndex] || ""}
                                  onChange={(e) =>
                                    handleBatchChange(lineIndex, e.target.value)
                                  }
                                >
                                  <MenuItem value="">בחר אצווה</MenuItem>
                                  {loadingSerials[lineIndex] ? (
                                    <MenuItem disabled>טוען אצוות...</MenuItem>
                                  ) : (
                                    serialNumbers[line.partname]?.map(
                                      (serial, idx) => (
                                        <MenuItem
                                          key={idx}
                                          value={serial.SERIALNAME}
                                        >
                                          {serial.SERIALNAME}
                                        </MenuItem>
                                      ),
                                    )
                                  )}
                                </Select>
                              </Box>
                              <Box>
                                <Typography variant="caption">
                                  הערה 2
                                </Typography>
                                <TextField
                                  size="small"
                                  multiline
                                  fullWidth
                                  sx={{ mt: 1, minWidth: "200px" }}
                                  value={noteValues[lineIndex] || ""}
                                  onChange={(e) =>
                                    handleNoteChange(lineIndex, e.target.value)
                                  }
                                />
                              </Box>
                            </Box>
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    );
  },
);

export default OrderTable;
